// src/App.jsx
import './App.css';
import Header from "./components/Header/Header.jsx";
import Footer from "./components/Footer/Footer.jsx";
import MainPage from "./components/MainPage/MainPage.jsx";
import { Route, Routes, useLocation } from "react-router-dom";
import Products from "./components/Products/Products.jsx";
import ProductDetails from "./components/ProductDetails/ProductDetails.jsx";
import { useEffect, useState } from "react";
import BasketWidget from "./components/BasketWidget/BasketWidget.jsx";
import Basket from "./components/Basket/Basket.jsx";
import Order from "./components/Order/Order.jsx";
import Account from "./components/Account/Account.jsx";
import Page404 from "./components/Page404/Page404.jsx";
import { getCart } from "./CartHandler.js";
import { useBasket } from "./context/BasketContext/BasketContext.jsx";
import Rules from "./components/Rules/Rules.jsx";
import PrivacyPolicy from "./components/PrivacyPolicy/PrivacyPolicy.jsx";
import CookiesPolicy from "./components/CookiesPolicy/CookiesPolicy.jsx";
import Notification from "./components/Notification/Notification.jsx";
import { useNotification } from "./context/NotificationContext/NotificationContext.jsx";
import { Helmet, HelmetProvider } from "react-helmet-async";
import CookieBanner from "./components/CookieBanner/CookieBanner.jsx";
import SnowAnimation from "./components/SnowAnimation/SnowAnimation.jsx";

function App() {
    const location = useLocation();
    const { displayNotification } = useNotification();
    const { basketVersion, bumpBasketVersion } = useBasket();
    const [displayBasketWidget, setDisplayBasketWidget] = useState(false);
    const [displayUserWidget, setDisplayUserWidget] = useState(false);
    const [loggedUser, setLoggedUser] = useState(null);
    const [basket, setBasket] = useState([]);

    useEffect(() => {
        const handler = () => bumpBasketVersion();
        window.addEventListener("cartUpdated", handler);
        return () => window.removeEventListener("cartUpdated", handler);
    }, [bumpBasketVersion]);

    useEffect(() => {
        let cancelled = false;
        const fetchBasket = async () => {
            try {
                const cart = await getCart();
                if (cancelled) return;
                if (cart?.items?.length) {
                    setBasket(
                        cart.items.map(line => ({
                            item: line.item,
                            quantity: line.quantity,
                        }))
                    );
                } else setBasket([]);
            } catch {
                setBasket([]);
            }
        };
        fetchBasket();
        return () => {
            cancelled = true;
        };
    }, [basketVersion]);

    useEffect(() => {
        const token = sessionStorage.getItem("customerAccessToken");
        if (token) setLoggedUser(true);
    }, []);

    const canonical = `https://lumestore.pl${location.pathname}${location.search || ""}`;

    const shouldRenderLayout =
        location.pathname === "/" ||
        location.pathname.startsWith("/produkty") ||
        location.pathname.startsWith("/produkt") ||
        location.pathname === "/koszyk" ||
        location.pathname === "/zamowienie" ||
        location.pathname.startsWith("/konto/") ||
        location.pathname === "/regulamin" ||
        location.pathname === "/polityka" ||
        location.pathname === "/ciasteczka";

    return (
        <HelmetProvider>
            <Helmet prioritizeSeoTags>
                <html lang="pl" />
                <title>LumeStore — Sklep online z produktami beauty</title>
                <meta
                    name="description"
                    content="LumeStore — profesjonalny sklep online z akcesoriami i produktami beauty dla stylistek rzęs. Sprawdź ofertę rzęs, klejów i akcesoriów."
                />
                <meta name="robots" content="index,follow" />
                <meta property="og:type" content="website" />
                <meta
                    property="og:title"
                    content="LumeStore — Sklep online z produktami beauty"
                />
                <meta
                    property="og:description"
                    content="Profesjonalne produkty dla stylistek rzęs i kosmetologów — rzęsy, kleje, pęsety i akcesoria."
                />
                <meta
                    property="og:image"
                    content="https://lumestore.pl/og-image.webp"
                />
                <meta property="og:url" content={canonical} />
                <meta property="og:site_name" content="LumeStore" />
                <meta property="og:locale" content="pl_PL" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="LumeStore — Sklep online z produktami beauty"
                />
                <meta
                    name="twitter:description"
                    content="Rzęsy, kleje, pęsety, akcesoria i wyposażenie salonów — LumeStore."
                />
                <meta
                    name="twitter:image"
                    content="https://lumestore.pl/og-image.webp"
                />
                <link rel="canonical" href={canonical} />
            </Helmet>

            <div className="app">
                {import.meta.env.VITE_IS_CHRISTMAS === true ? <SnowAnimation /> : <></>}
                <CookieBanner />
                {displayNotification && <Notification />}

                {shouldRenderLayout && (
                    <>
                        <Header
                            setDisplayBasketWidget={setDisplayBasketWidget}
                            displayUserWidget={displayUserWidget}
                            setDisplayUserWidget={setDisplayUserWidget}
                            loggedUser={loggedUser}
                            setLoggedUser={setLoggedUser}
                        />
                        <BasketWidget
                            setDisplayBasketWidget={setDisplayBasketWidget}
                            displayBasketWidget={displayBasketWidget}
                            basket={basket}
                        />
                    </>
                )}

                <Routes>
                    <Route path="/" element={<MainPage />} />
                    <Route path="/produkty" element={<Products />} />
                    <Route path="/produkty/:maincat" element={<Products />} />
                    <Route path="/produkty/:maincat/:subcat" element={<Products />} />
                    <Route path="/produkt" element={<ProductDetails />} />
                    <Route path="/produkt/:handle" element={<ProductDetails />} />
                    <Route path="/koszyk" element={<Basket basket={basket} />} />
                    <Route path="/zamowienie" element={<Order />} />
                    <Route
                        path="/konto/zamowienia/:id"
                        element={<Account currentTab="orders" />}
                    />
                    <Route
                        path="/konto/zamowienia"
                        element={<Account currentTab="orders" />}
                    />
                    <Route
                        path="/konto/opinie"
                        element={<Account currentTab="reviews" />}
                    />
                    <Route
                        path="/konto/dostawa"
                        element={<Account currentTab="shipment-details" />}
                    />
                    <Route
                        path="/konto/ustawienia"
                        element={<Account currentTab="settings" />}
                    />
                    <Route path="/regulamin" element={<Rules />} />
                    <Route path="/polityka" element={<PrivacyPolicy />} />
                    <Route path="/ciasteczka" element={<CookiesPolicy />} />
                    <Route path="*" element={<Page404 />} />
                    {/*<Route path="/test" element={<Test />}/>*/}
                </Routes>

                {shouldRenderLayout && <Footer />}
            </div>
        </HelmetProvider>
    );
}

export default App;